import { useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'app/hooks';
import {
  CategoryTO,
  SubCategoryTO,
  VocabularyTestSelectionItem,
  WordSetCategoryType,
} from 'app/types';
import { axiosInstance } from 'setup/axiosInstance';
import {
  ScopeSelectionMap,
  ScopeUniverseMap,
  buildFullSelectionFromUniverse,
  buildScopeKey,
  computeScopeSummary,
  toSelectionItems,
} from 'pages/VocabularyTest/scopeSelection';

export interface WordSetOption {
  label: string;
  wordSetType: WordSetCategoryType;
}

export const WORD_SET_OPTIONS: WordSetOption[] = [
  { label: 'Англійська лексика за рівнями', wordSetType: 'level-based' },
  { label: 'Найбільш поширені слова', wordSetType: 'most-common' },
  { label: 'Тематична лексика', wordSetType: 'topic-related' },
  { label: 'Фразові дієслова', wordSetType: 'phrasal-verbs' },
  { label: 'Словосполучення', wordSetType: 'collocations' },
  { label: 'Ідіоми', wordSetType: 'idioms' },
  { label: 'Неправильні дієслова', wordSetType: 'irregular-verb' },
];

export const DEFAULT_WORD_SET_TYPE: WordSetCategoryType = 'level-based';

const getIrregularVerbSubCategories = (
  categoryName: string,
): SubCategoryTO[] => [
  {
    addedWords: 0,
    learnedWords: 0,
    percentOfLearnedWords: 0,
    subcategoryDescription: '',
    subcategoryName: categoryName,
    totalWords: 0,
  },
];

export interface VocabularyTestScope {
  wordSetOptions: WordSetOption[];
  selectedWordSetType: WordSetCategoryType;
  setSelectedWordSetType: (type: WordSetCategoryType) => void;
  activeCategoryName: string;
  setActiveCategoryName: (name: string) => void;
  categories: CategoryTO[];
  activeCategory: CategoryTO | undefined;
  getSubCategories: (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => SubCategoryTO[];
  isLoadingSubCategories: (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => boolean;
  isSubCategorySelected: (
    wordSetType: WordSetCategoryType,
    categoryName: string,
    subCategoryName: string,
  ) => boolean;
  isCategoryFullySelected: (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => boolean;
  isCategoryPartiallySelected: (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => boolean;
  toggleCategory: (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => void;
  toggleSubCategory: (
    wordSetType: WordSetCategoryType,
    categoryName: string,
    subCategoryName: string,
  ) => void;
  selectedCount: number;
  catCount: number;
  isDefault: boolean;
  soleFullWordSetType: WordSetCategoryType | null;
  reset: () => void;
  /** `[]` while `isDefault` is true — the page dispatches `undefined`
   * scope in that case so the backend keeps deciding the full test on its
   * own, same as before this redesign. */
  selection: VocabularyTestSelectionItem[];
}

export const useVocabularyTestScope = (): VocabularyTestScope => {
  const wordSetCategories = useAppSelector(
    (store) => store.common.wordSetCategories,
  );

  const [selectedWordSetType, setSelectedWordSetTypeState] =
    useState<WordSetCategoryType>(DEFAULT_WORD_SET_TYPE);
  const [activeCategoryName, setActiveCategoryName] = useState('');
  const [subCategoriesByKey, setSubCategoriesByKey] = useState<
    Record<string, SubCategoryTO[]>
  >({});
  const [loadingKeys, setLoadingKeys] = useState<Record<string, boolean>>({});
  const [selectedSubCategories, setSelectedSubCategories] =
    useState<ScopeSelectionMap>({});
  const [defaultSeeded, setDefaultSeeded] = useState(false);

  const categories = useMemo(
    () => wordSetCategories[selectedWordSetType]?.data?.categories || [],
    [selectedWordSetType, wordSetCategories],
  );

  const activeCategory = useMemo(
    () =>
      categories.find(
        ({ categoryName }) => categoryName === activeCategoryName,
      ),
    [activeCategoryName, categories],
  );

  const loadSubCategories = async (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ): Promise<SubCategoryTO[]> => {
    const key = buildScopeKey(wordSetType, categoryName);

    if (wordSetType === 'irregular-verb') {
      const data = getIrregularVerbSubCategories(categoryName);
      setSubCategoriesByKey((prev) => ({ ...prev, [key]: data }));
      return data;
    }

    if (subCategoriesByKey[key]) return subCategoriesByKey[key];

    setLoadingKeys((prev) => ({ ...prev, [key]: true }));
    try {
      const { data } = await axiosInstance.get<SubCategoryTO[]>(
        `/wordset/${wordSetType}/${categoryName}`,
      );
      setSubCategoriesByKey((prev) => ({ ...prev, [key]: data }));
      return data;
    } finally {
      setLoadingKeys((prev) => ({ ...prev, [key]: false }));
    }
  };

  // Seed the default scope once the level-based categories are known: fetch
  // every one of their subcategory lists in parallel and pre-check them
  // all, so the panel opens with "the whole level-based list" selected —
  // mirroring the redesign's default state without changing what gets sent
  // to the backend for an untouched selection (see `selection` below).
  useEffect(() => {
    if (defaultSeeded) return;
    const levelBasedCategories =
      wordSetCategories['level-based']?.data?.categories || [];
    if (!levelBasedCategories.length) return;

    setDefaultSeeded(true);
    setActiveCategoryName(
      (prev) => prev || levelBasedCategories[0].categoryName,
    );

    Promise.all(
      levelBasedCategories.map(({ categoryName }) =>
        loadSubCategories('level-based', categoryName),
      ),
    ).then((results) => {
      const universe: ScopeUniverseMap = {};
      levelBasedCategories.forEach(({ categoryName }, i) => {
        universe[buildScopeKey('level-based', categoryName)] = results[i].map(
          ({ subcategoryName }) => subcategoryName,
        );
      });
      setSelectedSubCategories(
        buildFullSelectionFromUniverse(
          universe,
          levelBasedCategories.map((c) => c.categoryName),
          'level-based',
        ),
      );
    });
    // loadSubCategories closes over state that changes every render; this
    // effect only needs to (re-)run when the level-based categories arrive
    // or after a reset clears `defaultSeeded`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wordSetCategories, defaultSeeded]);

  const universe: ScopeUniverseMap = useMemo(() => {
    const map: ScopeUniverseMap = {};
    Object.entries(subCategoriesByKey).forEach(([key, subs]) => {
      map[key] = subs.map(({ subcategoryName }) => subcategoryName);
    });
    return map;
  }, [subCategoriesByKey]);

  const categoryNamesByWordSetType = useMemo(() => {
    const map: Record<string, string[]> = {};
    Object.entries(wordSetCategories).forEach(([type, state]) => {
      map[type] = (state?.data?.categories || []).map((c) => c.categoryName);
    });
    return map;
  }, [wordSetCategories]);

  const summary = useMemo(
    () =>
      computeScopeSummary(
        selectedSubCategories,
        universe,
        categoryNamesByWordSetType,
        DEFAULT_WORD_SET_TYPE,
      ),
    [selectedSubCategories, universe, categoryNamesByWordSetType],
  );

  const getSubCategories = (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => {
    const key = buildScopeKey(wordSetType, categoryName);
    return (
      subCategoriesByKey[key] ||
      (wordSetType === 'irregular-verb'
        ? getIrregularVerbSubCategories(categoryName)
        : [])
    );
  };

  const isLoadingSubCategories = (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => !!loadingKeys[buildScopeKey(wordSetType, categoryName)];

  const isSubCategorySelected = (
    wordSetType: WordSetCategoryType,
    categoryName: string,
    subCategoryName: string,
  ) =>
    (
      selectedSubCategories[buildScopeKey(wordSetType, categoryName)] || []
    ).includes(subCategoryName);

  const isCategoryFullySelected = (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => {
    const subCategories = getSubCategories(wordSetType, categoryName);
    const selected =
      selectedSubCategories[buildScopeKey(wordSetType, categoryName)] || [];
    return !!subCategories.length && selected.length === subCategories.length;
  };

  const isCategoryPartiallySelected = (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => {
    const selected =
      selectedSubCategories[buildScopeKey(wordSetType, categoryName)] || [];
    return (
      !!selected.length && !isCategoryFullySelected(wordSetType, categoryName)
    );
  };

  const toggleCategory = async (
    wordSetType: WordSetCategoryType,
    categoryName: string,
  ) => {
    const key = buildScopeKey(wordSetType, categoryName);
    const subCategories =
      subCategoriesByKey[key] ||
      (await loadSubCategories(wordSetType, categoryName));
    const subCategoryNames = subCategories.map(
      ({ subcategoryName }) => subcategoryName,
    );
    const currentSelected = selectedSubCategories[key] || [];
    const shouldSelectAll =
      !subCategoryNames.length ||
      currentSelected.length !== subCategoryNames.length;

    setActiveCategoryName(categoryName);
    setSelectedSubCategories((prev) => ({
      ...prev,
      [key]: shouldSelectAll ? subCategoryNames : [],
    }));
  };

  const toggleSubCategory = (
    wordSetType: WordSetCategoryType,
    categoryName: string,
    subCategoryName: string,
  ) => {
    const key = buildScopeKey(wordSetType, categoryName);
    setSelectedSubCategories((prev) => {
      const current = prev[key] || [];
      const next = current.includes(subCategoryName)
        ? current.filter((name) => name !== subCategoryName)
        : [...current, subCategoryName];
      return { ...prev, [key]: next };
    });
  };

  const setSelectedWordSetType = (wordSetType: WordSetCategoryType) => {
    setSelectedWordSetTypeState(wordSetType);
    const nextCategories =
      wordSetCategories[wordSetType]?.data?.categories || [];
    setActiveCategoryName(nextCategories[0]?.categoryName || '');
  };

  const reset = () => {
    setDefaultSeeded(false);
    setSelectedSubCategories({});
    setSelectedWordSetTypeState(DEFAULT_WORD_SET_TYPE);
  };

  useEffect(() => {
    if (!categories.length) {
      setActiveCategoryName('');
      return;
    }
    if (
      !categories.some(
        ({ categoryName }) => categoryName === activeCategoryName,
      )
    ) {
      setActiveCategoryName(categories[0].categoryName);
    }
  }, [activeCategoryName, categories]);

  useEffect(() => {
    if (activeCategoryName) {
      loadSubCategories(selectedWordSetType, activeCategoryName);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategoryName, selectedWordSetType]);

  return {
    wordSetOptions: WORD_SET_OPTIONS,
    selectedWordSetType,
    setSelectedWordSetType,
    activeCategoryName,
    setActiveCategoryName,
    categories,
    activeCategory,
    getSubCategories,
    isLoadingSubCategories,
    isSubCategorySelected,
    isCategoryFullySelected,
    isCategoryPartiallySelected,
    toggleCategory,
    toggleSubCategory,
    selectedCount: summary.selectedCount,
    catCount: summary.catCount,
    isDefault: summary.isDefault,
    soleFullWordSetType: summary.soleFullWordSetType,
    reset,
    selection: summary.isDefault ? [] : toSelectionItems(selectedSubCategories),
  };
};
